/**
 * BLRRD Pointwise Filters — Multi-point Curves
 *
 * Extension of the legacy 2-point curves panel to support 8+ user
 * control points with the same Fritsch-Carlson monotone cubic Hermite
 * interpolation already shipping in `public/blrrd-editor-v15.js`
 * (line 1637, `evalSpline`).
 *
 * Algorithm: Fritsch, F. N., & Carlson, R. E. (1980). "Monotone
 * Piecewise Cubic Interpolation." SIAM J. Numer. Anal., 17(2). The
 * tangents are clamped per-segment so the spline never overshoots a
 * user anchor — what colorists call "guaranteed-monotone curves".
 *
 * Pipeline slot: KERNEL-STAGE. Operates on the same buffer the legacy
 * curves panel feeds (scene-linear ACEScg with an ACEScct log-mapped
 * X-axis when channel === 'L'). For per-channel R/G/B it stays linear
 * — this matches the legacy code (Fritsch-Carlson on linear ACEScg).
 *
 * L* channel uses ACEScct encoding for the X-axis so the slider feel
 * matches Resolve / Baselight / Nuke colorists' expectations
 * (curves operate on log-perceptual luminance, NOT linear).
 *
 * Histogram helper is included here because the UI panel needs it as
 * a single import alongside the curve evaluator.
 *
 * Identity short-circuit: points = [[0,0],[1,1]] (straight line) →
 * returns the same Float32Array reference. Empty / single-point input
 * is also treated as identity.
 */

import { encodeACEScct, decodeACEScct } from '../../../color/aces.js';

const IDENTITY_EPS = 1e-7;
const LUT_SIZE = 1024;

/** Default curves params — identity (linear) ramp. */
export function defaultCurvesParams() {
    return { points: [[0, 0], [1, 1]], channel: 'rgb' };
}

/** True iff `params.points` is the identity ramp. */
export function isIdentityCurvesMulti(params) {
    if (!params) return true;
    const pts = params.points;
    if (!Array.isArray(pts) || pts.length < 2) return true;
    /* Must be exactly 2 anchor points sitting on the diagonal. */
    if (pts.length !== 2) return false;
    if (Math.abs(pts[0][0] - 0) > IDENTITY_EPS) return false;
    if (Math.abs(pts[0][1] - 0) > IDENTITY_EPS) return false;
    if (Math.abs(pts[1][0] - 1) > IDENTITY_EPS) return false;
    if (Math.abs(pts[1][1] - 1) > IDENTITY_EPS) return false;
    return true;
}

/**
 * Apply a multi-point curve to a Float32 RGBA buffer in place.
 *
 * @param {Float32Array} rgba       w*h*4 Float32, scene-linear
 * @param {number}       w
 * @param {number}       h
 * @param {object}       params     { points: [[x,y]...], channel }
 * @param {object}       [opts]     { selectionMask?: Float32Array(w*h) }
 * @returns {Float32Array}          same `rgba` reference
 */
export function applyCurvesMulti(rgba, w, h, params, opts) {
    validateBuffer(rgba, w, h);
    if (isIdentityCurvesMulti(params)) return rgba;

    const mask = opts && opts.selectionMask ? opts.selectionMask : null;
    if (mask && mask.length !== w * h) {
        throw new Error('applyCurvesMulti: selectionMask length mismatch');
    }
    const points  = params.points;
    const channel = params.channel || 'rgb';
    const lut     = buildCurveLUT(points, LUT_SIZE);
    const N = w * h;

    if (channel === 'L') {
        /* L* tab — apply curve on ACEScct-encoded luminance, then re-
         * spread to RGB by scaling all three channels by the ratio
         * f(L)/L. This preserves RGB ratio (hue) while shaping
         * brightness, matching Resolve's "Luma" curve behavior.
         */
        for (let p = 0; p < N; p++) {
            const i = p << 2;
            const m = mask ? mask[p] : 1;
            if (m <= 0) continue;
            const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2];
            /* Rec.709-style luminance — works on AP1 too because we
             * read coefficients from sRGB-bias which AP1→ACEScg
             * makes approximately preserve. */
            const Y = 0.2722 * r + 0.6741 * g + 0.0537 * b;
            const yLog = encodeACEScct(Math.max(Y, 0));
            const yLogPrime = lookupLUT(lut, yLog);
            const Yprime = decodeACEScct(yLogPrime);
            const k = Y > 1e-7 ? (Yprime / Y) : 1;
            const newR = r * k;
            const newG = g * k;
            const newB = b * k;
            if (m >= 1) {
                rgba[i]     = newR;
                rgba[i + 1] = newG;
                rgba[i + 2] = newB;
            } else {
                const inv = 1 - m;
                rgba[i]     = inv * r + m * newR;
                rgba[i + 1] = inv * g + m * newG;
                rgba[i + 2] = inv * b + m * newB;
            }
        }
        return rgba;
    }

    /* Per-channel / composite path — straight LUT lookup. */
    const doR = channel === 'rgb' || channel === 'r';
    const doG = channel === 'rgb' || channel === 'g';
    const doB = channel === 'rgb' || channel === 'b';
    for (let p = 0; p < N; p++) {
        const i = p << 2;
        const m = mask ? mask[p] : 1;
        if (m <= 0) continue;
        const newR = doR ? lookupLUT(lut, rgba[i])     : rgba[i];
        const newG = doG ? lookupLUT(lut, rgba[i + 1]) : rgba[i + 1];
        const newB = doB ? lookupLUT(lut, rgba[i + 2]) : rgba[i + 2];
        if (m >= 1) {
            rgba[i]     = newR;
            rgba[i + 1] = newG;
            rgba[i + 2] = newB;
        } else {
            const inv = 1 - m;
            rgba[i]     = inv * rgba[i]     + m * newR;
            rgba[i + 1] = inv * rgba[i + 1] + m * newG;
            rgba[i + 2] = inv * rgba[i + 2] + m * newB;
        }
    }
    return rgba;
}

/**
 * Compute a histogram of one channel.
 *
 * @param {Float32Array} rgba
 * @param {number}       w
 * @param {number}       h
 * @param {string}       channel  'r'|'g'|'b'|'luminance'
 * @param {number}       [bins=256]
 * @returns {Uint32Array}
 */
export function computeHistogram(rgba, w, h, channel, bins) {
    validateBuffer(rgba, w, h);
    const B = bins || 256;
    const hist = new Uint32Array(B);
    const N = w * h;
    const cIdx = channel === 'r' ? 0
        : channel === 'g' ? 1
        : channel === 'b' ? 2
        : -1;
    for (let p = 0; p < N; p++) {
        const i = p << 2;
        let v;
        if (cIdx >= 0) {
            v = rgba[i + cIdx];
        } else {
            /* luminance */
            v = 0.2722 * rgba[i] + 0.6741 * rgba[i + 1] + 0.0537 * rgba[i + 2];
        }
        if (v < 0) v = 0;
        else if (v > 1) v = 1;
        let bin = Math.round(v * (B - 1));
        if (bin < 0) bin = 0;
        else if (bin >= B) bin = B - 1;
        hist[bin]++;
    }
    return hist;
}

/**
 * Build a Float32 LUT from a multi-point Fritsch-Carlson monotone cubic.
 * Exported for unit tests and for the legacy editor's curves panel.
 *
 * @param {Array<[number,number]>} points
 * @param {number}                 size
 * @returns {Float32Array}
 */
export function buildCurveLUT(points, size) {
    const sorted = sortPoints(points);
    const lut = new Float32Array(size);
    for (let i = 0; i < size; i++) {
        const t = i / (size - 1);
        lut[i] = evalMonotoneCubic(sorted, t);
    }
    return lut;
}

/**
 * Look up a Float32 curve value with linear interpolation.
 * @param {Float32Array} lut
 * @param {number}       v
 * @returns {number}
 */
export function lookupLUT(lut, v) {
    const N = lut.length - 1;
    if (v <= 0) return lut[0];
    if (v >= 1) return lut[N];
    const idx = v * N;
    const lo = idx | 0;
    const frac = idx - lo;
    return lut[lo] + (lut[lo + 1] - lut[lo]) * frac;
}

/**
 * Fritsch-Carlson monotone cubic Hermite spline. Pure function; safe
 * to call standalone. Matches the legacy `evalSpline` in
 * blrrd-editor-v15.js but takes [x,y] tuples instead of {x,y}.
 */
export function evalMonotoneCubic(pts, t) {
    const n = pts.length;
    if (n < 2) return t;
    if (t <= pts[0][0])     return pts[0][1];
    if (t >= pts[n - 1][0]) return pts[n - 1][1];
    if (n === 2) {
        const p0 = pts[0], p1 = pts[1];
        return p0[1] + (p1[1] - p0[1]) * (t - p0[0]) / (p1[0] - p0[0]);
    }
    /* Slopes between successive anchors. */
    const dx = new Array(n - 1);
    const dy = new Array(n - 1);
    const m  = new Array(n - 1);
    for (let i = 0; i < n - 1; i++) {
        dx[i] = pts[i + 1][0] - pts[i][0];
        dy[i] = pts[i + 1][1] - pts[i][1];
        m[i]  = dy[i] / dx[i];
    }
    /* Tangents — three-point harmonic mean (Fritsch-Carlson rule). */
    const tg = new Array(n);
    tg[0]     = m[0];
    tg[n - 1] = m[n - 2];
    for (let i = 1; i < n - 1; i++) {
        if (m[i - 1] * m[i] <= 0) tg[i] = 0;
        else tg[i] = (m[i - 1] + m[i]) / 2;
    }
    /* Enforce monotonicity (Fritsch-Carlson clamp). */
    for (let i = 0; i < n - 1; i++) {
        if (Math.abs(m[i]) < 1e-10) {
            tg[i] = 0;
            tg[i + 1] = 0;
            continue;
        }
        const a = tg[i] / m[i];
        const b = tg[i + 1] / m[i];
        const s2 = a * a + b * b;
        if (s2 > 9) {
            const tau = 3 / Math.sqrt(s2);
            tg[i]     = tau * a * m[i];
            tg[i + 1] = tau * b * m[i];
        }
    }
    /* Find segment. */
    let idx = 0;
    for (let i = 0; i < n - 1; i++) {
        if (t >= pts[i][0] && t <= pts[i + 1][0]) {
            idx = i;
            break;
        }
    }
    const hSeg = dx[idx];
    const s = (t - pts[idx][0]) / hSeg;
    const s2v = s * s;
    const s3v = s2v * s;
    const h00 =  2 * s3v - 3 * s2v + 1;
    const h10 =      s3v - 2 * s2v + s;
    const h01 = -2 * s3v + 3 * s2v;
    const h11 =      s3v -     s2v;
    return h00 * pts[idx][1]
         + h10 * hSeg * tg[idx]
         + h01 * pts[idx + 1][1]
         + h11 * hSeg * tg[idx + 1];
}

function sortPoints(points) {
    const out = points.map(p => [p[0], p[1]]);
    out.sort((a, b) => a[0] - b[0]);
    return out;
}

function validateBuffer(rgba, w, h) {
    if (!(rgba instanceof Float32Array)) {
        throw new TypeError('curves-multi: rgba must be Float32Array');
    }
    if (rgba.length !== w * h * 4) {
        throw new Error('curves-multi: rgba.length !== w*h*4');
    }
}
